import { app } from "@microsoft/teams-js";
import { TeamsQuestionTemplate, TeamsSurveyTypes, TeamsUser } from "@teams/types";
import { QuesionnaireTemplateCreateDTO, QuestionnaireTemplateDTO } from "@teams/types/api";

import { FetchError } from "../services/ChannelApi";
import {
  FetchResult,
  deserializeTemplateDTO,
  encodeApiUrl,
  fetchApiWithContext,
  isSuccess,
} from "./ChannelApi";

export const templatesApi = {
  create: async (
    context: app.Context,
    template: QuesionnaireTemplateCreateDTO
  ): Promise<FetchResult<{ templateId: string }>> => {
    return fetchApiWithContext<{ templateId: string }>(context, encodeApiUrl`/v1/templates`, {
      method: "POST",
      body: JSON.stringify(template),
    });
  },

  update: async (
    context: app.Context,
    templateId: string,
    template: QuesionnaireTemplateCreateDTO
  ): Promise<FetchResult<void>> => {
    return fetchApiWithContext<void>(context, encodeApiUrl`/v1/templates/${templateId}`, {
      method: "PUT",
      body: JSON.stringify(template),
    });
  },

  copyTemplate: async (
    context: app.Context,
    templateId: string
  ): Promise<FetchResult<QuestionnaireTemplateDTO>> => {
    const template = await fetchApiWithContext<QuestionnaireTemplateDTO>(
      context,
      encodeApiUrl`/v1/templates/${templateId}/copy`,
      { method: "post" }
    );

    if (isSuccess(template)) {
      deserializeTemplateDTO(template);
    }

    return template;
  },

  getTemplate: async (
    context: app.Context,
    templateId: string
  ): Promise<FetchResult<QuestionnaireTemplateDTO>> => {
    const template = await fetchApiWithContext<QuestionnaireTemplateDTO>(
      context,
      encodeApiUrl`/v1/templates/${templateId}`
    );

    if (isSuccess(template)) {
      deserializeTemplateDTO(template);
    }

    return template;
  },

  getPersonalTemplates: async (
    context: app.Context,
    surveyTypes: TeamsSurveyTypes[]
  ): Promise<FetchResult<QuestionnaireTemplateDTO[]>> => {
    const queryParams = new URLSearchParams();
    queryParams.append("types", surveyTypes.join(","));

    const templates = await fetchApiWithContext<QuestionnaireTemplateDTO[]>(
      context,
      encodeApiUrl`/v1/templates/personal`,
      {
        queryParams,
      }
    );
    if (isSuccess(templates)) {
      templates.forEach(deserializeTemplateDTO);
    }

    return templates;
  },

  getGlobalTemplates: async (
    context: app.Context
  ): Promise<FetchResult<QuestionnaireTemplateDTO[]>> => {
    const templates = await fetchApiWithContext<QuestionnaireTemplateDTO[]>(
      context,
      encodeApiUrl`/v1/templates/global`
    );
    if (isSuccess(templates)) {
      templates.forEach(deserializeTemplateDTO);
    }

    return templates;
  },

  deleteTemplate: (context: app.Context, templateId: string): Promise<FetchResult<void>> => {
    return fetchApiWithContext(context, encodeApiUrl`/v1/templates/${templateId}`, {
      method: "delete",
    });
  },

  addOwner: async (
    context: app.Context,
    templateId: string,
    user: TeamsUser
  ): Promise<FetchResult<void | FetchError>> => {
    const update = await fetchApiWithContext<void | FetchError>(
      context,
      encodeApiUrl`/v1/templates/${templateId}/owner`,
      { method: "post", body: JSON.stringify(user) }
    );

    if (isSuccess(update)) {
      return;
    }

    return update;
  },

  removeOwner: async (
    context: app.Context,
    templateId: string,
    userAADId: string
  ): Promise<FetchResult<void | FetchError>> => {
    const update = await fetchApiWithContext<void | FetchError>(
      context,
      encodeApiUrl`/v1/templates/${templateId}/owner/${userAADId}`,
      { method: "delete" }
    );

    if (isSuccess(update)) {
      return;
    }

    return update;
  },

  addUser: async (
    context: app.Context,
    templateId: string,
    user: TeamsUser
  ): Promise<FetchResult<void | FetchError>> => {
    const update = await fetchApiWithContext<void | FetchError>(
      context,
      encodeApiUrl`/v1/templates/${templateId}/user`,
      { method: "post", body: JSON.stringify(user) }
    );

    if (isSuccess(update)) {
      return;
    }

    return update;
  },

  removeUser: async (
    context: app.Context,
    templateId: string,
    userAADId: string
  ): Promise<FetchResult<void | FetchError>> => {
    const update = await fetchApiWithContext<void | FetchError>(
      context,
      encodeApiUrl`/v1/templates/${templateId}/user/${userAADId}`,
      { method: "delete" }
    );

    if (isSuccess(update)) {
      return;
    }

    return update;
  },

  getQuestionTemplateCatgories: async (
    context: app.Context,
    scope: "trivia"
  ): Promise<FetchResult<{ categories: string[] } | FetchError>> => {
    return fetchApiWithContext(context, encodeApiUrl`/v1/questiontemplates/${scope}/categories`);
  },

  getRandomQuestionTemplates: async (
    context: app.Context,
    scope: "icebreaker" | "hot-take",
    size?: number
  ): Promise<FetchResult<{ templates: TeamsQuestionTemplate[] } | FetchError>> => {
    const queryParams = new URLSearchParams();
    if (size) {
      queryParams.append("size", size.toString());
    }

    return fetchApiWithContext(context, encodeApiUrl`/v1/questiontemplates/${scope}/random`, {
      queryParams,
    });
  },
};
